import { useState, type ChangeEvent } from 'react';
import { Calendar, ChevronDown, MapPin, ImageIcon, Camera, X, Check } from 'lucide-react';
import { Modal, Button, inputClass } from '@/components/ui';
import { PickerSheet, PickerRow } from '@/features/wardrobe/components/PickerSheet';
import { OCCASIONS } from '@/lib/constants';
import { todayISO, cn } from '@/lib/utils';
import { downscaleToBase64 } from '@/lib/image';
import api from '@/lib/api';

export interface LogWearValues {
  wornDate: string;
  occasion: string;
  locationText: string;
  notes: string;
}

interface LogWearModalProps {
  isOpen: boolean;
  onClose: () => void;
  fitName: string;
  pieceCount: number;
  /** Logs the wear and returns the new wear id (so photos can be attached). */
  onSubmit: (values: LogWearValues, photos: string[]) => Promise<string | null>;
  defaultLocation?: string;
}

// Shared "Log Wear" bottom sheet used across Available, Detail pages.
export function LogWearModal({
  isOpen,
  onClose,
  fitName,
  pieceCount,
  onSubmit,
  defaultLocation = '',
}: LogWearModalProps) {
  const [wornDate, setWornDate] = useState(todayISO());
  const [occasion, setOccasion] = useState('');
  const [locationText, setLocationText] = useState(defaultLocation);
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState<string[]>([]);
  const [showOccasion, setShowOccasion] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setWornDate(todayISO());
    setOccasion('');
    setLocationText(defaultLocation);
    setNotes('');
    setPhotos([]);
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const handlePhotoUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const encoded: string[] = [];
    for (const file of Array.from(files)) {
      try {
        encoded.push(await downscaleToBase64(file));
      } catch {
        /* ignore single-file failures */
      }
    }
    setPhotos((prev) => [...prev, ...encoded]);
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!wornDate) return;
    setSaving(true);
    setError(null);
    const wearId = await onSubmit({ wornDate, occasion, locationText, notes }, photos);
    if (wearId === null) {
      setError('Failed to log wear. Please try again.');
      setSaving(false);
      return;
    }
    setSuccess(true);
    setSaving(false);
    setTimeout(handleClose, 1200);
  };

  if (!isOpen) return null;

  if (success) {
    return (
      <Modal isOpen onClose={handleClose}>
        <div className="flex flex-col items-center justify-center py-10">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-base font-bold text-foreground">Wear Logged!</h3>
          <p className="text-sm text-muted-foreground mt-1">Updated {pieceCount} items</p>
        </div>
      </Modal>
    );
  }

  return (
    <>
      <Modal
        isOpen
        onClose={handleClose}
        title="Log Wear"
        footer={
          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button fullWidth onClick={handleSubmit} disabled={!wornDate || saving}>
              {saving ? 'Logging…' : 'Log Wear'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="bg-secondary/50 rounded-xl p-3">
            <p className="text-sm font-medium text-foreground">{fitName}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{pieceCount} items will be updated</p>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Date *</label>
            <div className="relative">
              <input
                type="date"
                value={wornDate}
                onChange={(e) => setWornDate(e.target.value)}
                className={cn(inputClass, 'pl-10')}
              />
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Occasion</label>
            <button
              type="button"
              onClick={() => setShowOccasion(true)}
              className={cn(inputClass, 'flex items-center justify-between')}
            >
              <span className={occasion ? 'text-foreground' : 'text-muted-foreground'}>
                {occasion ? OCCASIONS.find((o) => o.id === occasion)?.label : 'Select occasion'}
              </span>
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Location</label>
            <div className="relative">
              <input
                type="text"
                value={locationText}
                onChange={(e) => setLocationText(e.target.value)}
                placeholder="Where did you wear this?"
                className={cn(inputClass, 'pl-10')}
              />
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any notes about this wear…"
              rows={3}
              className={cn(inputClass, 'resize-none')}
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-1.5">
              <ImageIcon className="w-4 h-4" />
              Photos
            </label>
            {photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-2">
                {photos.map((photo, idx) => (
                  <div key={idx} className="relative aspect-square rounded-lg overflow-hidden bg-secondary">
                    <img src={photo} alt={`Wear ${idx + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setPhotos((prev) => prev.filter((_, i) => i !== idx))}
                      className="absolute top-1 right-1 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center text-white"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <label className="flex items-center justify-center gap-2 py-3 border-2 border-dashed border-border rounded-xl text-sm text-muted-foreground hover:border-primary/30 hover:text-foreground transition-all cursor-pointer">
              <Camera className="w-4 h-4" />
              <span>{photos.length > 0 ? 'Add more photos' : 'Add photos'}</span>
              <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" />
            </label>
          </div>
        </div>
      </Modal>

      <PickerSheet isOpen={showOccasion} onClose={() => setShowOccasion(false)} title="Select Occasion">
        <div className="space-y-1">
          {OCCASIONS.map((o) => (
            <PickerRow
              key={o.id}
              label={o.label}
              selected={occasion === o.id}
              onClick={() => {
                setOccasion(o.id);
                setShowOccasion(false);
              }}
            />
          ))}
        </div>
      </PickerSheet>
    </>
  );
}

// Helper: upload base64 photos for a freshly created wear.
export async function uploadWearPhotos(wearId: string, photos: string[]): Promise<void> {
  for (const image of photos) {
    try {
      await api.post('/photos', { ownerType: 'fit_wear', ownerId: wearId, image });
    } catch {
      /* best-effort; ignore individual photo failures */
    }
  }
}
